import type { SpinResponse } from "@slot/client-engine";
import type { ReplayRoundProvider } from "./ReplayRoundProvider";

/**
 * HTTP transport implementation of `ReplayRoundProvider`.
 *
 * Calls `GET {baseUrl}{replayPath}/{roundId}` and returns the `SpinResponse`
 * the backend cached when the round was originally evaluated. Read-only —
 * the endpoint has no wallet / state-transition side effects.
 *
 * Non-2xx responses throw. The caller decides the UX:
 * - `fetchReplayRound` (`?replay=`) logs it, and `playReplayRound` shows the replay-failed modal.
 * - `restorePendingRound` logs and skips the restore flow on throw.
 */
export interface BackendReplayRoundProviderConfiguration {
  /** e.g. "http://localhost:3000". Trailing slash tolerated. */
  baseUrl: string;

  /** e.g. "/api/replay". Appended with "/<encoded-roundId>". */
  replayPath: string;

  /** Optional headers (auth, session). Called per request. */
  getRequestHeaders?: () => Record<string, string>;

  /** Default 10_000ms. `AbortSignal.timeout`, armed through the body. */
  requestTimeoutMs?: number;
}

const DEFAULT_REQUEST_TIMEOUT_MS = 10_000;

export class BackendReplayRoundProvider implements ReplayRoundProvider {
  private readonly configuration: BackendReplayRoundProviderConfiguration;

  public constructor(configuration: BackendReplayRoundProviderConfiguration) {
    this.configuration = configuration;
  }

  public async fetchRoundForReplay(roundId: string): Promise<SpinResponse> {
    const url = this.buildUrl(roundId);
    const timeoutMs = this.configuration.requestTimeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS;

    // The body is read under the same deadline: `fetch` resolves on the headers, and a body that
    // never arrives used to hang the pending-round restore and the `?replay=` boot for good.
    let response: Response;
    let rawBody: string;
    try {
      response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(this.configuration.getRequestHeaders?.() ?? {}),
        },
        signal: AbortSignal.timeout(timeoutMs),
      });
      rawBody = await response.text();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      throw new Error(`BackendReplayRoundProvider: network error calling ${url}: ${message}`);
    }

    if (!response.ok) {
      const detail = this.extractRejectDetail(rawBody);
      throw new Error(
        `BackendReplayRoundProvider: ${url} returned ${response.status} ${response.statusText}${detail ? ` (${detail})` : ""}`
      );
    }

    let parsed: unknown;
    try {
      parsed = rawBody.length > 0 ? JSON.parse(rawBody) : undefined;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      throw new Error(
        `BackendReplayRoundProvider: failed to parse response body from ${url}: ${message}`
      );
    }

    if (!isShapedSpinResponse(parsed)) {
      throw new Error(
        `BackendReplayRoundProvider: 2xx response from ${url} is not a SpinResponse (missing roundSnapshot).`
      );
    }

    return parsed;
  }

  private buildUrl(roundId: string): string {
    const base = this.configuration.baseUrl.replace(/\/$/, "");
    const path = this.configuration.replayPath.startsWith("/")
      ? this.configuration.replayPath
      : `/${this.configuration.replayPath}`;
    const trimmedPath = path.replace(/\/$/, "");
    return `${base}${trimmedPath}/${encodeURIComponent(roundId)}`;
  }

  private extractRejectDetail(rawBody: string): string | null {
    if (rawBody.length === 0) return null;
    try {
      const parsed: unknown = JSON.parse(rawBody);
      if (!isRecord(parsed)) return null;
      const metadata = (parsed as { responseMetadata?: unknown }).responseMetadata;
      if (!isRecord(metadata)) return null;
      const statusMessage = (metadata as { statusMessage?: unknown }).statusMessage;
      if (typeof statusMessage === "string" && statusMessage.length > 0) return statusMessage;
      return null;
    } catch {
      return null;
    }
  }
}

function isShapedSpinResponse(value: unknown): value is SpinResponse {
  if (!isRecord(value)) return false;
  const snapshot = (value as { roundSnapshot?: unknown }).roundSnapshot;
  return isRecord(snapshot) && typeof (snapshot as { roundIdentifier?: unknown }).roundIdentifier === "string";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
